import React, {useMemo} from "react";
import {observer_mgl} from "mobx-graphlink";
import {MapState} from "../../../../Store/main/maps/@MapState.js";
import {DMap} from "../../../../Server/DB/maps/@Map.js";
import {Canvas, useFrame, useThree} from "@react-three/fiber";
import {Plane} from "@react-three/drei";
import * as THREE from "three";
import {GetMapObject} from "../../../../Server/DB/mapObjects.js";
import {GuideShape, ObjMesh} from "../../../../Server/DB/mapObjects/@MapObject/ObjMesh.js";
import {ObjPath} from "../../../../Server/DB/mapObjects/@MapObject/ObjPath.js";
import {TransformZoomLevel} from "../MapSurface/MapControls.js";
import {Vec2} from "../../../../Utils/ThreeJS/VectorStructs.js";
import {ResolveObjPosInWorld} from "../../../../Server/DB/mapObjects/$mapObject.js";
import {observer_mgl_r3} from "../../../../Utils/ThreeJS/General.js";
import {mapZIndexes} from "../MapSurface/MapZIndexes.js";

export const PolygonGuide = observer_mgl_r3((props: {map: DMap, mapState: MapState, forTool: "meshModify" | "pathModify"})=>{
	const {map, mapState, forTool} = props;
	if (!mapState.settings.general.showGuideGrids) return null;
	const obj = GetMapObject(mapState.activeObject, map.id);
	if (!obj) return null;
	const objPos = ResolveObjPosInWorld(obj);

	const objMesh = ObjMesh.GetSelected(obj);
	if (forTool == "meshModify" && objMesh && objMesh.guide_size != null) {
		if (objMesh.guide_shape == GuideShape.square) return <Grid {...{mapState}} objPos={objPos.ToVec2()} squareSize={objMesh.guide_size}/>;
	}

	const objPath = ObjPath.GetSelected(obj);
	if (forTool == "pathModify" && objPath && objPath.guide_size != null) {
		if (objPath.guide_shape == GuideShape.square) return <Grid {...{mapState}} objPos={objPos.ToVec2()} squareSize={objPath.guide_size}/>;
	}

	return null;
});

const Grid = observer_mgl_r3((props: {mapState: MapState, objPos: Vec2, squareSize: number})=>{
	const {mapState, objPos, squareSize} = props;
	const camera_base = useThree(state=>state.camera);
	const camera = camera_base as THREE.OrthographicCamera;
	const viewSize = Math.max(Math.abs(camera.right - camera.left), Math.abs(camera.top - camera.bottom)); // the width/height (whichever is higher) of the camera"s view
	const viewSize_withZoomApplied = viewSize / TransformZoomLevel(mapState.camZoom_0ms, 1.5);
	// always keep the grid-count even, so that point 0,0 is at an intersection point in the grid (also add some padding, since texture doesn't have a white-line at its bottom/right edge)
	const viewSize_withPadding = viewSize_withZoomApplied.CeilingTo(squareSize * 2) + (squareSize * 2);
	const tileCount = viewSize_withPadding / squareSize;
	const texture = useMemo(()=>GenerateGridTexture(256, tileCount), [tileCount]);
	if (squareSize <= 0) return null; // if the squareSize is zero or negative, grid can't meaningfully be rendered, so cancel

	const camPos_alignedWithObjPos = mapState.camPos_0ms.NewZ(z=>mapZIndexes.polygonGuideGrid)
		.NewX(x=>x.RoundTo(squareSize) + (objPos.x % squareSize))
		.NewY(y=>y.RoundTo(squareSize) + (objPos.y % squareSize))
		.To3JPos();

	return (
		<Plane args={[viewSize_withPadding, viewSize_withPadding]}
			position={camPos_alignedWithObjPos}
		>
			<meshBasicMaterial attach="material" transparent map={texture}/>
		</Plane>
	);
});

const GenerateGridTexture = (canvasSize: number, tileCount: number)=>{
	const canvas = document.createElement("canvas");
	canvas.width = canvasSize;
	canvas.height = canvasSize;
	const context = canvas.getContext("2d", {alpha: true}) as CanvasRenderingContext2D;

	context.clearRect(0, 0, canvasSize, canvasSize); // clear the canvas to make it transparent

	context.strokeStyle = "rgba(255,255,255,.5)";
	context.lineWidth = 10;

	for (let x = 0; x < canvasSize; x += canvasSize) { // only draw one stroke
		context.beginPath();
		context.moveTo(x, 0);
		context.lineTo(x, canvasSize);
		context.stroke();
	}
	for (let y = 0; y < canvasSize; y += canvasSize) { // only draw one stroke
		context.beginPath();
		context.moveTo(0, y);
		context.lineTo(canvasSize, y);
		context.stroke();
	}

	const texture = new THREE.CanvasTexture(canvas);
	texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
	texture.repeat.set(tileCount, tileCount);

	return texture;
};